import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Scissors, ChevronDown, ChevronUp } from "lucide-react";
import { getIndian, getPortfolio } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, ScoreBar, Loader, Delta } from "../components/Primitives";
import { ratingColor, fmtPct, fmtNum, POS, NEG } from "../lib/format";

const TIERS = [
  { id: "all", label: "All" }, { id: "large", label: "Large Cap" }, { id: "mid", label: "Mid Cap" },
  { id: "small", label: "Small Cap" }, { id: "etf", label: "ETF/Index" }, { id: "debt", label: "Debt" },
];

export default function IndianScreener() {
  const { money } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["indian"], queryFn: getIndian, refetchInterval: 45000 });
  const { data: pf } = useQuery({ queryKey: ["portfolio"], queryFn: getPortfolio });
  const [tier, setTier] = useState("all");
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [sort, setSort] = useState({ key: "composite", dir: "desc" });
  const [sel, setSel] = useState(null);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.instruments.filter((x) =>
      (tier === "all" || x.cap_tier === tier) &&
      x.scores.composite >= minScore &&
      (q === "" || x.name.toLowerCase().includes(q.toLowerCase()) || x.symbol.toLowerCase().includes(q.toLowerCase()))
    );
    const get = (x) => sort.key === "composite" ? x.scores.composite
      : sort.key === "pe" ? x.f.pe : sort.key === "roe" ? x.f.roe
      : sort.key === "price" ? x.price_inr : x.scores[sort.key] ?? x.scores.composite;
    r = [...r].sort((a, b) => (get(b) - get(a)) * (sort.dir === "desc" ? 1 : -1));
    return r;
  }, [data, tier, q, minScore, sort]);

  if (isLoading || !data) return <Loader />;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const SortH = ({ k, children, className = "" }) => (
    <th className={`pb-2 font-medium cursor-pointer select-none ${className}`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{children}
        {sort.key === k && (sort.dir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}</span>
    </th>
  );

  const avgScore = (data.instruments.reduce((s, x) => s + x.scores.composite, 0) / data.instruments.length).toFixed(1);
  const strongBuys = data.instruments.filter((x) => x.scores.rating === "Strong Buy" || x.scores.rating === "Accumulate").length;
  const tlh = pf?.tax_loss_harvest || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4"><Overline>Universe</Overline><div className="font-mono text-2xl font-semibold mt-1">{data.instruments.length}</div><div className="text-xs text-[#64748b] mt-1">NSE/BSE instruments scored</div></Card>
        <Card className="p-4"><Overline>Avg Composite</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#00e5ff]">{avgScore}</div><div className="text-xs text-[#64748b] mt-1">Factor model score</div></Card>
        <Card className="p-4"><Overline>Buy Signals</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#00e676]">{strongBuys}</div><div className="text-xs text-[#64748b] mt-1">Accumulate or stronger</div></Card>
        <Card className="p-4"><Overline>Tax-Loss Ideas</Overline><div className="font-mono text-2xl font-semibold mt-1 text-[#ffb300]">{tlh.length}</div><div className="text-xs text-[#64748b] mt-1">Harvesting opportunities</div></Card>
      </div>

      <Card className="p-4" testid="screener-controls">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-[#2d3c59] px-3 py-1.5 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-[#64748b]" />
            <input data-testid="screener-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticker or company…"
              className="bg-transparent outline-none text-sm w-full text-[#f8fafc] placeholder:text-[#64748b]" />
          </div>
          <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden">
            {TIERS.map((t) => (
              <button key={t.id} data-testid={`tier-${t.id}`} onClick={() => setTier(t.id)}
                className={`px-3 py-1.5 text-xs transition-colors ${tier === t.id ? "bg-[#1a2338] text-[#00e5ff]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>{t.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 min-w-[180px]">
            <span className="overline whitespace-nowrap">Min Score {minScore}</span>
            <input type="range" min="0" max="90" value={minScore} onChange={(e) => setMinScore(+e.target.value)} className="flex-1" data-testid="min-score-slider" />
          </div>
        </div>
      </Card>

      <Card className="p-5" testid="screener-table">
        <div className="scroll-x">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left overline border-b border-[#1e293b]">
                <th className="pb-2 font-medium w-8">#</th>
                <th className="pb-2 font-medium">Instrument</th>
                <th className="pb-2 font-medium">Sector</th>
                <SortH k="price" className="text-right">Price</SortH>
                <SortH k="roe" className="text-right">ROE</SortH>
                <SortH k="pe" className="text-right">P/E</SortH>
                <SortH k="fundamental" className="text-right">Fund.</SortH>
                <SortH k="technical" className="text-right">Tech.</SortH>
                <SortH k="macro" className="text-right">Macro</SortH>
                <SortH k="composite">Composite</SortH>
                <th className="pb-2 font-medium text-right">Rating</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x, i) => (
                <tr key={x.id} data-testid={`screener-table-row-${i}`} onClick={() => setSel(x)}
                  className="border-b border-[#131a2a] hover:bg-[#1a2338] cursor-pointer transition-colors">
                  <td className="py-2.5 font-mono text-[#64748b]">{i + 1}</td>
                  <td className="py-2.5"><div className="font-medium text-[#f8fafc]">{x.name}</div><div className="font-mono text-[11px] text-[#64748b]">{x.symbol}</div></td>
                  <td className="py-2.5 text-xs text-[#94a3b8]">{x.sector}</td>
                  <td className="py-2.5 text-right font-mono text-[#94a3b8]">{money(x.price_inr, { compact: true })}</td>
                  <td className="py-2.5 text-right font-mono" style={{ color: x.f.roe >= 15 ? POS : "#94a3b8" }}>{x.f.roe ? `${x.f.roe}%` : "—"}</td>
                  <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.f.pe || "—"}</td>
                  <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.scores.fundamental}</td>
                  <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.scores.technical}</td>
                  <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.scores.macro}</td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold" style={{ color: ratingColor(x.scores.rating) }}>{x.scores.composite}</span>
                      <div className="w-16"><ScoreBar value={x.scores.composite} color={ratingColor(x.scores.rating)} /></div>
                    </div>
                  </td>
                  <td className="py-2.5 text-right"><Badge color={ratingColor(x.scores.rating)}>{x.scores.rating}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="text-center py-10 text-sm text-[#64748b]">No instruments match your filters.</div>}
        </div>
      </Card>

      {tlh.length > 0 && (
        <Card className="p-5" testid="tax-loss-card">
          <div className="flex items-center gap-2 mb-3"><Scissors className="w-4 h-4 text-[#ffb300]" /><Overline>Tax-Loss Harvesting Opportunities</Overline></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tlh.map((t) => (
              <div key={t.instrument_id} className="rounded-lg border border-[#1e293b] p-3" data-testid={`tlh-${t.instrument_id}`}>
                <div className="text-sm font-medium text-[#f8fafc]">{t.name}</div>
                <div className="flex items-center justify-between mt-2 font-mono text-xs">
                  <span className="text-[#ff3b30]">Loss {money(-t.loss_inr, { compact: true })}</span>
                  <span className="text-[#00e676]">Save ~{money(t.est_saving_inr, { compact: true })}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#64748b] mt-3">Estimated tax offset using Indian LTCG 12.5% (equity) / 30% (crypto). Harvesting realizes losses to offset taxable gains. Not tax advice.</p>
        </Card>
      )}

      <AnimatePresence>
        {sel && <DetailModal x={sel} onClose={() => setSel(null)} money={money} />}
      </AnimatePresence>
    </div>
  );
}

function DetailModal({ x, onClose, money }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const pillars = [
    { k: "Fundamental", v: x.scores.fundamental, c: "#00e676" },
    { k: "Technical", v: x.scores.technical, c: "#00e5ff" },
    { k: "Macro", v: x.scores.macro, c: "#a855f7" },
  ];
  const fund = [["ROE", x.f.roe ? `${x.f.roe}%` : "—"], ["ROCE", x.f.roce ? `${x.f.roce}%` : "—"], ["D/E", x.f.de || "—"], ["Int. Cover", x.f.interest_cov || "—"], ["P/E", x.f.pe || "—"], ["PEG", x.f.peg || "—"], ["EV/EBITDA", x.f.ev_ebitda || "—"], ["Div Yield", `${x.f.div_yield}%`]];
  const tech = [["50 EMA", x.t.ema50], ["200 EMA", x.t.ema200], ["RSI (14)", x.t.rsi], ["MACD slope", x.t.macd_slope], ["Rel. Strength", fmtPct(x.t.rel_strength)], ["Beta", x.t.beta], ["Max DD", fmtPct(x.t.max_drawdown, 0)], ["ATR %", `${x.t.atr_pct}%`]];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(4,7,13,0.7)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()} className="ao-card w-full max-w-lg p-6" data-testid="screener-detail-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold text-[#f8fafc]">{x.name}</div>
            <div className="font-mono text-xs text-[#64748b] mt-0.5">{x.symbol} · {x.sector} · {money(x.price_inr)}</div>
          </div>
          <button onClick={onClose} className="text-[#64748b] hover:text-[#f8fafc]" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Badge color={ratingColor(x.scores.rating)}>{x.scores.rating}</Badge>
          <span className="font-mono text-2xl font-semibold" style={{ color: ratingColor(x.scores.rating) }}>{x.scores.composite}<span className="text-sm text-[#64748b]">/100</span></span>
          {x.scores.bull_regime && <Badge color={POS}>BULL REGIME · Price &gt; 200 EMA</Badge>}
        </div>
        <div className="space-y-2.5 mt-4">
          {pillars.map((p) => (
            <div key={p.k}>
              <div className="flex justify-between text-xs mb-1"><span className="text-[#94a3b8]">{p.k}</span><span className="font-mono" style={{ color: p.c }}>{p.v}</span></div>
              <ScoreBar value={p.v} color={p.c} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-5 mt-5">
          <div>
            <Overline className="mb-2">Fundamentals</Overline>
            <div className="space-y-1.5 font-mono text-xs">{fund.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-[#64748b]">{k}</span><span className="text-[#f8fafc]">{v}</span></div>)}</div>
          </div>
          <div>
            <Overline className="mb-2">Technicals</Overline>
            <div className="space-y-1.5 font-mono text-xs">{tech.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-[#64748b]">{k}</span><span className="text-[#f8fafc]">{v}</span></div>)}</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
